/** @jsx jsx */
import { jsx, Container, Box } from 'theme-ui';
import { FunctionComponent, useState } from 'react';
import { graphql, PageProps } from 'gatsby';
import Modal from 'react-modal';
import { useDeepCompareEffect, useDeepCompareMemo } from 'use-deep-compare';

import Layout from '@codersclan/gatsby-theme-airfleet/src/components/layout/layout';
import SectionsLoader from '@codersclan/gatsby-theme-airfleet/src/components/sectionsLoader';

// Types
import { PageDetails as PageDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/page';
import { PostDetails as PostDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/post';

// Components
import CustomGrid from '../../components/customGrid';
import TripleCardList from '../../components/tripleCardList';
import Card from '../../components/card';
import CTAButton from '../../components/ctaButton';
import Filter from '../../components/filter';
import Head from '../../components/head';

// Hooks
import usePagination from '../../hooks/usePagination';
import useFilter from '../../hooks/useFilter';
import useSEOPropsResolver from '../../hooks/useSEOPropsResolver';

// Helpers
import {
  getCategory,
  getCtaLabel,
  getDefaultCategory,
} from '../../helpers/postHelper';
import { getExcerptToCard } from '../../helpers/general';
import merge from 'lodash.merge';

Modal.setAppElement('#___gatsby');

interface Data {
  pagesData: {
    nodes: Array<PageDetailsType>;
  };
  resourcesData: {
    nodes: PostDetailsType[];
  };
  postData: {
    nodes: PostDetailsType[];
  };
  allWp: {
    nodes: Array<{
      themeGeneralSettings: {
        optionsGeneral: {
          metaTags: {
            value: string;
          };
        };
      };
    }>;
  };
}

type Props = PageProps<Data>;

const defaultOptionDropdown = { label: 'All', value: 'all' };
type DropdownOptions = typeof defaultOptionDropdown;

function sortByDate(a: PostDetailsType, b: PostDetailsType) {
  return new Date(b.date).getTime() - new Date(a.date).getTime();
}

function sortByName(a: DropdownOptions, b: DropdownOptions) {
  return a.label.localeCompare(b.label);
}

function filterResults(
  resource: PostDetailsType,
  topic: string | undefined,
  filterBy: string | undefined,
  searchBy: string | undefined,
): boolean {
  let condition = true;
  if (topic && topic !== 'all') {
    condition =
      condition && topic === 'blog'
        ? resource.nodeType === 'Post'
        : resource.categories.nodes?.[0]?.slug === topic;
  }

  if (filterBy && filterBy !== 'all') {
    condition =
      condition &&
      resource.nodeType === 'Post' &&
      resource.postCategories.nodes?.[0]?.slug === filterBy;
  }

  if (searchBy) {
    const regExp = new RegExp(searchBy, 'im');
    condition = condition && regExp.test(resource.title);
  }

  return condition;
}

function getTopicOptions(allResourcesData: PostDetailsType[]) {
  const output: DropdownOptions[] = [{ label: 'Blog', value: 'blog' }];
  allResourcesData.forEach(r => {
    const option = getDefaultCategory(r);
    if (!output.some(a => a.value === option.slug)) {
      output.push({
        label: option.name,
        value: option.slug,
      });
    }
  });
  output.sort(sortByName);
  output.unshift(defaultOptionDropdown);
  return output;
}

function getFilterOptions(
  allResourcesData: PostDetailsType[],
  topic: string | undefined,
) {
  if (topic && topic !== 'all' && topic !== 'blog') {
    return [defaultOptionDropdown];
  }
  const output: DropdownOptions[] = [];
  allResourcesData.forEach(r => {
    if (r.nodeType === 'Post') {
      const option = getCategory(r);
      if (
        option.slug !== 'blog' &&
        !output.some(a => a.value === option.slug)
      ) {
        output.push({
          label: option.name,
          value: option.slug,
        });
      }
    }
  });
  output.sort(sortByName);
  output.unshift(defaultOptionDropdown);
  return output;
}

function getResourceCard(resource: PostDetailsType) {
  return (
    <Card
      key={resource.id}
      categorySlug={getCategory(resource).slug}
      category={getCategory(resource).name}
      title={resource.title}
      content={getExcerptToCard(resource)}
      cta={{
        label: getCtaLabel(resource),
        url: resource.uri,
        target: '_self',
      }}
      image={resource.featuredImage?.node}
    />
  );
}

interface UseResourceListProperties {
  allResourcesData: Array<PostDetailsType>;
  limit: number;
  topic?: string;
  filterBy?: string;
  searchBy?: string;
}

function useResourceList({
  allResourcesData,
  topic,
  filterBy,
  searchBy,
  limit,
}: UseResourceListProperties) {
  const [resourceList, setResourceList] = useState<Array<PostDetailsType>>([]);
  const [selected, setSelected] = useState<Array<PostDetailsType>>([]);
  const [others, setOthers] = useState<Array<PostDetailsType>>([]);
  const [othersList, setOthersList] = useState<Array<PostDetailsType>>([]);
  const [showMore, setShowMore] = useState(false);

  useDeepCompareEffect(() => {
    setResourceList(
      allResourcesData
        .filter(resource => filterResults(resource, topic, filterBy, searchBy))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        ),
    );
  }, [allResourcesData, topic, filterBy, searchBy]);

  useDeepCompareEffect(() => {
    setSelected(resourceList.slice(0, 6));
    setOthers(resourceList.slice(6));
  }, [resourceList]);

  useDeepCompareEffect(() => {
    setOthersList(others.slice(0, limit));
    setShowMore(limit < others.length);
  }, [others, limit]);

  return {
    resourceList,
    selected,
    others,
    othersList,
    showMore,
  };
}

const PageTemplate: FunctionComponent<Props> = ({ data }) => {
  const { limit, resetPagination, loadMore } = usePagination();
  const {
    filterBy: { current: filterBy, set: setFilterBy },
    search: { current: searchBy },
    topic: { current: topic },
  } = useFilter();
  const { pagesData, resourcesData, postData, allWp } = data;
  const page = pagesData.nodes[0];
  const sections = page?.templateAllPages?.sections || [];
  const allResourcesData = useDeepCompareMemo(
    () => [...postData.nodes, ...resourcesData.nodes].sort(sortByDate),
    [postData, resourcesData],
  );
  const {
    resourceList,
    selected,
    others,
    othersList,
    showMore,
  } = useResourceList({
    allResourcesData,
    topic,
    filterBy,
    searchBy,
    limit,
  });

  useDeepCompareEffect(() => {
    resetPagination();
  }, [resourceList]);

  const [
    headerSection,
    resourceDownloadSection,
    prefooterCTASection,
  ] = sections;

  const topicOptions = useDeepCompareMemo(
    () => getTopicOptions(allResourcesData),
    [allResourcesData],
  );

  const filterByOptions = useDeepCompareMemo(
    () => getFilterOptions(allResourcesData, topic),
    [topic, allResourcesData],
  );

  useDeepCompareEffect(() => {
    const currentExist = filterByOptions.some(o => o.value === filterBy);

    if (!currentExist) {
      setFilterBy(undefined);
    }
  }, [filterByOptions]);

  const { metaTags } = allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral;
  const seoProps = metaTags
    ? merge({}, useSEOPropsResolver(page), { metaTags: metaTags.value })
    : useSEOPropsResolver(page);

  return (
    <Layout>
      <Head {...seoProps} />

      {/* Top Sections */}
      <SectionsLoader sections={[headerSection]} />

      {/* Custom Section */}
      <Container sx={{ padding: ['40px 24px 0px', '82px 0px 0px'] }}>
        <Filter
          topicsOptions={topicOptions}
          filterByOptions={filterByOptions}
          sx={{ marginBottom: ['40px', '82px'] }}
        />

        <TripleCardList>{selected?.map(getResourceCard)}</TripleCardList>

        {/* Middle Section */}
        {resourceDownloadSection && (
          <Box sx={{ marginX: ['-20px', null, '0px'] }}>
            <SectionsLoader sections={[resourceDownloadSection]} />
          </Box>
        )}

        {/* Custom Section */}
        {othersList?.length > 0 && (
          <CustomGrid
            sx={{
              marginTop: !resourceDownloadSection ? ['43px', '84px'] : '0px',
            }}
          >
            {othersList.map(getResourceCard)}
          </CustomGrid>
        )}

        {showMore && others.length > 0 && (
          <CTAButton
            key={othersList.length}
            type="button"
            alternative
            sx={{ margin: ['57px auto 0px', '80px auto 0px'] }}
            onClick={loadMore}
          >
            Load More
          </CTAButton>
        )}
      </Container>

      <Box sx={{ marginTop: ['49px', '80px'] }}>
        {/* Bottom Sections */}
        <SectionsLoader sections={[prefooterCTASection]} />
      </Box>
    </Layout>
  );
};

export default PageTemplate;

export const pageQuery: void = graphql`
  query($slug: String) {
    pagesData: allWpPage(filter: { slug: { eq: $slug } }) {
      nodes {
        ...PageDetailsWithSEOFragment
        templateAllPages {
          sections {
            ...HeroV2Fragment
            ...ResourcesFeaturedDownloadFragment
            ...PreFooterCTAFragment
          }
        }
      }
    }
    resourcesData: allWpResource {
      nodes {
        ...BasicResourceFragment
      }
    }
    postData: allWpPost {
      nodes {
        ...BasicPostFragment
      }
    }
    allWp {
      nodes {
        themeGeneralSettings {
          optionsGeneral {
            metaTags {
              value
            }
          }
        }
      }
    }
  }
`;
